import { Skeleton } from '@/components/atoms';
import { WEB_PAGE_TOP_BELOW_HEADER } from '@/core/constants/webLayout';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

export interface PropertiesDetailsScreenSkeletonProps {
  isMobile: boolean;
  carouselHeight: number;
}

function AccordionSkeleton({
  compact = false,
  isMobile = false,
}: Readonly<{
  compact?: boolean;
  isMobile?: boolean;
}>) {
  const colors = useUIColors();
  const amenityCount = isMobile ? 6 : 5;
  const amenitySkeletonKeys = isMobile
    ? ['m1', 'm2', 'm3', 'm4', 'm5', 'm6']
    : ['d1', 'd2', 'd3', 'd4', 'd5'];

  return (
    <View
      style={[
        styles.accordion,
        { backgroundColor: colors.backgroundLevel1 },
        compact && styles.accordionCompact,
      ]}
    >
      <View style={styles.accordionHeader}>
        <Skeleton width={24} height={24} borderRadius={6} />
        <Skeleton width={compact ? 120 : 140} height={compact ? 18 : 22} />
        <View style={styles.accordionHeaderSpacer} />
        <Skeleton width={24} height={24} borderRadius={6} />
      </View>
      <View style={styles.accordionBody}>
        {!compact && <Skeleton width="40%" height={16} />}
        {!compact && (
          <View style={styles.unitInfoRow}>
            <Skeleton width={72} height={48} borderRadius={8} />
            <Skeleton width={72} height={48} borderRadius={8} />
            <Skeleton width={72} height={48} borderRadius={8} />
          </View>
        )}
        {!compact && (
          <>
            <Skeleton width={140} height={18} style={styles.floorPlanLine} />
            <Skeleton width="35%" height={16} />
            <View style={styles.amenitiesGrid}>
              {amenitySkeletonKeys.slice(0, amenityCount).map((skelKey) => (
                <Skeleton
                  key={skelKey}
                  width={isMobile ? '30%' : '18%'}
                  height={56}
                  borderRadius={8}
                />
              ))}
            </View>
          </>
        )}
        {compact && <Skeleton width="100%" height={56} borderRadius={12} />}
      </View>
    </View>
  );
}

function PriceSummarySkeleton({ standalone = false }: Readonly<{ standalone?: boolean }>) {
  const colors = useUIColors();

  return (
    <View style={styles.priceSummary}>
      {!standalone && (
        <View style={styles.priceSummaryHeader}>
          <Skeleton width={24} height={24} borderRadius={6} />
          <Skeleton width={100} height={22} />
        </View>
      )}
      <View
        style={[
          styles.priceBox,
          {
            backgroundColor: colors.backgroundLevel1,
            borderColor: colors.outlineDisabled,
          },
        ]}
      >
        <View style={styles.priceBoxColumn}>
          <Skeleton width={72} height={14} />
          <Skeleton width={120} height={24} style={{ marginTop: 4 }} />
        </View>
        <View style={styles.priceBoxColumnEnd}>
          <Skeleton width={110} height={12} />
          <Skeleton width={140} height={16} style={{ marginTop: 4 }} />
        </View>
      </View>
    </View>
  );
}

function ActionCardSkeleton() {
  const colors = useUIColors();

  return (
    <View style={[styles.actionCard, { backgroundColor: colors.backgroundLevel2 }]}>
      <View style={styles.actionCardHeader}>
        <Skeleton width={56} height={56} borderRadius={12} />
        <View style={styles.actionCardText}>
          <Skeleton width="70%" height={20} />
          <Skeleton width="100%" height={14} style={{ marginTop: 8 }} />
          <Skeleton width="90%" height={14} style={{ marginTop: 4 }} />
        </View>
      </View>
      <Skeleton width="100%" height={44} borderRadius={8} />
    </View>
  );
}

function StickyFooterSkeleton() {
  const colors = useUIColors();

  return (
    <View
      style={[
        styles.stickyFooter,
        {
          backgroundColor: colors.backgroundLevel1,
          borderTopColor: colors.outlineDisabled,
        },
      ]}
    >
      <Skeleton height={48} borderRadius={4} style={styles.stickyFooterButton} />
      <Skeleton height={48} borderRadius={4} style={styles.stickyFooterButton} />
    </View>
  );
}

/**
 * Skeleton layout for the web unit details screen.
 * Mirrors desktop two-column and mobile single-column layouts.
 */
export function PropertiesDetailsScreenSkeleton({
  isMobile,
  carouselHeight,
}: Readonly<PropertiesDetailsScreenSkeletonProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  const gallerySkeleton = (
    <Skeleton width="100%" height={carouselHeight} borderRadius={12} />
  );

  const headerSkeleton = (
    <View style={[styles.unitHeader, isMobile && styles.unitHeaderMobile]}>
      <View style={styles.titleRow}>
        <Skeleton width="75%" height={isMobile ? 28 : 34} />
        <Skeleton width={24} height={24} borderRadius={6} />
      </View>
      {!isMobile && <Skeleton width={220} height={18} />}
    </View>
  );

  const aboutSkeleton = <AccordionSkeleton isMobile={isMobile} />;
  const documentsSkeleton = <AccordionSkeleton compact isMobile={isMobile} />;

  const sidebarSkeleton = (
    <View
      style={[styles.sidebarCard, { backgroundColor: colors.backgroundLevel1 }]}
    >
      <PriceSummarySkeleton />
      <ActionCardSkeleton />
      <ActionCardSkeleton />
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <ScrollView
        style={[
          styles.contentArea,
          isRTL ? styles.contentAreaRTL : styles.contentAreaLTR,
        ]}
        contentContainerStyle={[
          styles.contentScroll,
          isMobile && styles.contentScrollMobile,
          isRTL ? styles.contentScrollGutterRTL : styles.contentScrollGutterLTR,
          isMobile && styles.contentScrollWithFooter,
        ]}
      >
        {isMobile ? (
          <>
            {gallerySkeleton}
            {headerSkeleton}
            <PriceSummarySkeleton standalone />
            {aboutSkeleton}
            {documentsSkeleton}
          </>
        ) : (
          <View style={styles.contentRow}>
            <View style={styles.leftColumn}>
              {gallerySkeleton}
              {headerSkeleton}
              {aboutSkeleton}
              {documentsSkeleton}
            </View>
            <View style={styles.rightColumn}>{sidebarSkeleton}</View>
          </View>
        )}
      </ScrollView>

      {isMobile && <StickyFooterSkeleton />}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentArea: {
    flex: 1,
  },
  contentAreaLTR: {
    marginRight: -16,
  },
  contentAreaRTL: {
    marginLeft: -16,
  },
  contentScroll: {
    gap: 16,
    paddingHorizontal: 24,
    paddingTop: WEB_PAGE_TOP_BELOW_HEADER,
    paddingBottom: 24,
  },
  contentScrollMobile: {
    gap: 12,
    paddingHorizontal: 0,
  },
  contentScrollGutterLTR: {
    paddingRight: 16,
  },
  contentScrollGutterRTL: {
    paddingLeft: 16,
  },
  contentScrollWithFooter: {
    paddingBottom: 16,
  },
  contentRow: {
    flexDirection: 'row',
    gap: 24,
    alignItems: 'flex-start',
  },
  leftColumn: {
    flex: 729,
    gap: 22,
  },
  rightColumn: {
    flex: 442,
  },
  unitHeader: {
    gap: 8,
  },
  unitHeaderMobile: {
    marginBottom: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  accordion: {
    borderRadius: 12,
    overflow: 'hidden',
    paddingBottom: 24,
  },
  accordionCompact: {
    paddingBottom: 16,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
  },
  accordionHeaderSpacer: {
    flex: 1,
  },
  accordionBody: {
    paddingHorizontal: 16,
    gap: 16,
  },
  unitInfoRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  floorPlanLine: {
    alignSelf: 'center',
  },
  amenitiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  priceSummary: {
    gap: 12,
  },
  priceSummaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingTop: 16,
    paddingHorizontal: 12,
  },
  priceBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    borderWidth: 0.5,
    borderRadius: 12,
    padding: 16,
  },
  priceBoxColumn: {
    gap: 4,
  },
  priceBoxColumnEnd: {
    flex: 1,
    alignItems: 'flex-end',
    gap: 4,
  },
  sidebarCard: {
    borderRadius: 12,
    paddingTop: 8,
    paddingHorizontal: 12,
    paddingBottom: 24,
    gap: 12,
  },
  actionCard: {
    borderRadius: 16,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 24,
  },
  actionCardHeader: {
    flexDirection: 'row',
    gap: 16,
  },
  actionCardText: {
    flex: 1,
  },
  stickyFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    marginHorizontal: -16,
    marginBottom: 0,
  },
  stickyFooterButton: {
    flex: 1,
  },
});

export default PropertiesDetailsScreenSkeleton;
